import React, { useRef } from "react";
import PropTypes from "prop-types";
import styled, { keyframes, ThemeProvider } from "styled-components";
import { space, layout, typography, variant } from "styled-system";
import { themeGet } from "@styled-system/theme-get";
import { pick } from "lodash";

// Utility function to create checkbox variant configs
const createCheckboxVariants = (colorMap) => {
  return {
    default: colorMap.default,
    success: colorMap.success,
    warning: colorMap.warning,
    danger: colorMap.danger,
    primary: colorMap.primary,
    white: colorMap.white,
  };
};

// Color mappings for different checkbox parts
const getCheckboxColors = (props) => ({
  default: themeGet("colors.subTextBody")(props),
  success: themeGet("colors.success")(props),
  warning: themeGet("colors.warning")(props),
  danger: themeGet("colors.danger")(props),
  primary: themeGet("colors.primary")(props),
  white: themeGet("colors.white")(props),
});

const getCheckboxFocusColors = (props) => ({
  default: themeGet("colors.black20")(props),
  success: themeGet("colors.success30")(props),
  warning: themeGet("colors.warning30")(props),
  danger: themeGet("colors.danger30")(props),
  primary: themeGet("colors.primary30")(props),
  white: themeGet("colors.white30")(props),
});

/* Animations */
const checkboxOn = keyframes`
  0% {
    box-shadow:
      0 0 0 10px,
      10px -10px 0 10px,
      32px 0px 0 20px,
      0px 32px 0 20px,
      -5px 5px 0 10px,
      15px 2px 0 11px;
  }
  50% {
    box-shadow:
      0 0 0 10px,
      10px -10px 0 10px,
      32px 0px 0 20px,
      0px 32px 0 20px,
      -5px 5px 0 10px,
      20px 2px 0 11px;
  }
  100% {
    box-shadow:
      0 0 0 10px,
      10px -10px 0 10px,
      32px 0px 0 20px,
      0px 32px 0 20px,
      -5px 5px 0 10px,
      20px -12px 0 11px;
  }
`;

const checkboxOff = keyframes`
  0% {
    box-shadow:
      0 0 0 10px,
      10px -10px 0 10px,
      32px 0px 0 20px,
      0px 32px 0 20px,
      -5px 5px 0 10px,
      20px -12px 0 11px,
      0 0 0 0 inset;
  }
  25% {
    box-shadow:
      0 0 0 10px,
      10px -10px 0 10px,
      32px 0px 0 20px,
      0px 32px 0 20px,
      -5px 5px 0 10px,
      20px -12px 0 11px,
      0 0 0 0 inset;
  }
  50% {
    transform: rotate(45deg);
    margin-top: -4px;
    margin-left: 6px;
    width: 0px;
    height: 0px;
    box-shadow:
      0 0 0 10px,
      10px -10px 0 10px,
      32px 0px 0 20px,
      0px 32px 0 20px,
      -5px 5px 0 10px,
      15px 2px 0 11px,
      0 0 0 0 inset;
  }
  51% {
    transform: rotate(0deg);
    margin-top: -2px;
    margin-left: -2px;
    width: 20px;
    height: 20px;
    box-shadow:
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0px 0px 0 10px inset;
  }
  100% {
    transform: rotate(0deg);
    margin-top: -2px;
    margin-left: -2px;
    width: 20px;
    height: 20px;
    box-shadow:
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0px 0px 0 0px inset;
  }
`;

const Item = styled.div`
  ${space}
  ${layout}
  display: block;
  transform: translateZ(0);
  color: ${(props) => {
    const colors = getCheckboxColors(props);
    return props.variant === "white" ? colors.white : colors.default;
  }};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
`;

const Control = styled.input.attrs({
  type: "checkbox",
})`
  opacity: 0;
  position: absolute;
  margin: 0;
  z-index: -1;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;

  &:focus {
    + div {
      border-radius: 2px;
      ${(props) => {
        const shadow = themeGet("shadows.thickOutline")(props);
        const focusColors = getCheckboxFocusColors(props);
        return variant({
          variants: createCheckboxVariants({
            default: { boxShadow: `${shadow} ${focusColors.default}` },
            success: { boxShadow: `${shadow} ${focusColors.success}` },
            warning: { boxShadow: `${shadow} ${focusColors.warning}` },
            danger: { boxShadow: `${shadow} ${focusColors.danger}` },
            primary: { boxShadow: `${shadow} ${focusColors.primary}` },
            white: { boxShadow: `${shadow} ${focusColors.white}` },
          }),
        })(props);
      }}
    }
  }

  /* Targeting box */
  + div {
    transition: ${themeGet("transition.transitionDefault")};
    &::before {
      ${(props) => {
        const colors = getCheckboxColors(props);
        return variant({
          variants: createCheckboxVariants({
            default: { backgroundColor: colors.default },
            success: { backgroundColor: colors.success },
            warning: { backgroundColor: colors.warning },
            danger: { backgroundColor: colors.danger },
            primary: { backgroundColor: colors.primary },
            white: { backgroundColor: colors.white },
          }),
        })(props);
      }}
    }
    > div {
      ${(props) => {
        const colors = getCheckboxColors(props);
        return variant({
          variants: createCheckboxVariants({
            default: { color: colors.default },
            success: { color: colors.success },
            warning: { color: colors.warning },
            danger: { color: colors.danger },
            primary: { color: colors.primary },
            white: { color: colors.white },
          }),
        })(props);
      }}
    }
  }

  /* Targeting Check */
  /* NOTE: styled-components v6 requires the & prefix for pseudo-selectors to maintain proper CSS specificity */
  /* Without &, :checked and :focus selectors may not apply correctly */
  &:focus + div div::after {
    opacity: 0.2;
  }

  &:checked {
    + div div::before {
      box-shadow:
        0 0 0 10px,
        10px -10px 0 10px,
        32px 0px 0 20px,
        0px 32px 0 20px,
        -5px 5px 0 10px,
        20px -12px 0 11px;
      .animate& {
        animation: ${checkboxOn} 300ms forwards ease-out;
      }
    }
  }

  &:not(:checked) + div div::before {
    .animate& {
      animation: ${checkboxOff} 300ms forwards ease-out;
    }
  }
`;

const Box = styled.div`
  position: relative;
  height: 20px;
  width: 20px;

  &::before {
    content: "";
    display: block;
    position: absolute;
    left: 8px;
    top: 8px;
    height: 4px;
    width: 4px;
    border-radius: 100%;
    z-index: 1;
    opacity: 0;
    margin: 0;
    pointer-events: none;
    /*transform: scale3d(2.3, 2.3, 1);*/
    background-color: ${(props) => themeGet("colors.black50")(props)};
  }
`;

const Check = styled.div`
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid;
  border-radius: ${(props) => themeGet("radii.1")(props)};
  overflow: hidden;
  z-index: 1;
  color: ${(props) => themeGet("colors.subText")(props)};

  ${(props) => {
    const colors = getCheckboxColors(props);
    return variant({
      variants: createCheckboxVariants({
        default: { color: colors.default },
        success: { color: colors.success },
        warning: { color: colors.warning },
        danger: { color: colors.danger },
        primary: { color: colors.primary },
        white: { color: colors.white },
      }),
    })(props);
  }}

  &::before {
    content: "";
    position: absolute;
    transform: rotate(45deg);
    display: block;
    margin-top: -4px;
    margin-left: 6px;
    width: 0;
    height: 0;
    box-shadow:
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0,
      0 0 0 0 inset;
    /*animation: checkboxOff 300ms forwards ease-out;*/
    /* Safari only fix for checkbox */
    @media not all and (min-resolution: 0.001dpcm) {
      @supports (-webkit-appearance: none) {
        width: 1px;
        height: 1px;
      }
    }
  }
`;

const Text = styled.div`
  font-size: ${(props) => themeGet("fontSizes.2")(props)};
  padding-left: 8px;
  ${typography}
`;
/**
 * The default checkbox (or inverted if on dark background) should be used for the majority of the UI; however, the coloured ones can be used in situations where the colour corresponds with some indication of status, e.g. in a task list, green could denote task completed, red could denote task overdue.
 */
export default function Checkbox({
  name,
  label,
  colour,
  variant: variantProp,
  disabled,
  checked,
  onClick,
  onChange,
  theme,
  ariaLabel,
  ...props
}) {
  const inputRef = useRef(null);

  // Support both colour (legacy) and variant props for backward compatibility
  const variant = variantProp || colour || "default";

  // Warn if using deprecated colour prop
  if (colour && !variantProp) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "Checkbox: The `colour` prop is deprecated. Please use `variant` instead. `colour` will be removed in a future version."
      );
    }
  }

  const handleClick = () => {
    if (!inputRef.current || disabled) {
      return;
    }
    inputRef.current.classList.add("animate");
  };

  const handleAnimationEnd = () => {
    if (!inputRef.current || disabled) {
      return;
    }

    inputRef.current.classList.remove("animate");
  };

  const component = (
    <Item
      variant={variant}
      {...props}
      onClick={handleClick}
      onAnimationEnd={handleAnimationEnd}
    >
      <Label disabled={disabled}>
        <Control
          name={name}
          variant={variant}
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          ref={inputRef}
          aria-label={ariaLabel}
        />
        <Box variant={variant} onClick={onClick}>
          <Check variant={variant} />
        </Box>
        <Text {...pick(props, typography.propNames)}>{label}</Text>
      </Label>
    </Item>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{component}</ThemeProvider>
  ) : (
    component
  );
}

Checkbox.propTypes = {
  /** Sets the colour of the checkbox. Colours used are the design system standard colours. @deprecated Use `variant` instead. */
  colour: PropTypes.oneOf(["success", "warning", "danger", "primary", "white"]),
  /** Sets the variant of the checkbox. Colours used are the design system standard colours. */
  variant: PropTypes.oneOf([
    "success",
    "warning",
    "danger",
    "primary",
    "white",
    "default",
  ]),
  /** Applies disabled attribute and styling */
  disabled: PropTypes.bool,
  /** Applies checked attribute and styling */
  checked: PropTypes.bool,
  /** Input name attribute (should be unique id) */
  name: PropTypes.string,
  /** On checkbox input change handler */
  onChange: PropTypes.func,
  /** On checkbox click handler */
  onClick: PropTypes.func,
  /** Text label to display beside the checkbox */
  label: PropTypes.string,
  // ariaLabel prop must be specified if label is not provided
  ariaLabel: (props, propName) => {
    if (!props.label && (props[propName] == null || props[propName] === "")) {
      return new Error(
        `Missing prop \`${propName}\` not specified for Checkbox component. When \`label\` is not provided, \`${propName}\` is required.`
      );
    }
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid propType for \`${propName}\` supplied to Checkbox component. Expected \`string\`, received \`${typeof props[
          propName
        ]}\`.`
      );
    }
    return null;
  },
  /** Specifies the system design theme. */
  theme: PropTypes.object,
};
